import type { AssetManifest, PetCopyWire, PetStateWire, PetsResponse } from '@kenocyber/shared';
import { PET_MAX_EVOLUTION } from '@kenocyber/shared';
import { staticAssetUrl } from '../game/assets';
import { PetStars } from './PetStars';
import { rarityLabel, rarityPaint, rarityStyle } from './rarity';

/**
 * The pets the account owns, and what each one is doing.
 *
 * One pet may be active at a time, and that is a property of the vault rather
 * than of this screen: ACTIVATE hands the slot over, so there is never a
 * "deactivate the old one first" dance for a player to get wrong.
 *
 * Evolution is MANUAL. Two copies at the same evolution become one of the
 * next, and nothing does it automatically - a duplicate is a thing the player
 * may want to keep.
 */

interface PetsTabProps {
  readonly pets: PetsResponse | null;
  readonly manifest: AssetManifest;
  readonly selectedId: string | null;
  readonly onSelect: (definitionId: string) => void;
}

/** The art of the evolution a card should show: the active one, else the best. */
export function displayEvolution(pet: PetStateWire): number {
  const active = pet.copies.find((copy) => copy.active);
  if (active) {
    return active.evolution;
  }
  return pet.copies.reduce((best, copy) => Math.max(best, copy.evolution), 1);
}

/**
 * The two copies EVOLVE would spend: same evolution, lowest first, never
 * reserved, never at the cap.
 *
 * Merging the weakest duplicates first is the move that costs the player
 * nothing they are currently benefiting from - and it never auto-selects the
 * active copy unless that is genuinely the only pair.
 */
export function firstEvolvablePair(pet: PetStateWire): readonly [PetCopyWire, PetCopyWire] | null {
  const byEvolution = new Map<number, PetCopyWire[]>();
  for (const copy of pet.copies) {
    if (copy.evolution >= PET_MAX_EVOLUTION || copy.reserved) {
      continue;
    }
    const bucket = byEvolution.get(copy.evolution);
    if (bucket) {
      bucket.push(copy);
    } else {
      byEvolution.set(copy.evolution, [copy]);
    }
  }
  for (const evolution of [...byEvolution.keys()].sort((a, b) => a - b)) {
    // Inactive copies first: if a pair exists that does not touch the active
    // pet, spend that one.
    const bucket = (byEvolution.get(evolution) ?? [])
      .slice()
      .sort((a, b) => Number(a.active) - Number(b.active));
    const [first, second] = bucket;
    if (first && second) {
      return [first, second];
    }
  }
  return null;
}

export function PetsTab({ pets, manifest, selectedId, onSelect }: PetsTabProps) {
  if (!pets) {
    return (
      <p className="inv-state" data-testid="pets-loading">
        Carregando pets…
      </p>
    );
  }
  return (
    <div className="art-grid" data-testid="pets-grid">
      {pets.pets.map((pet) => {
        const owned = pet.copies.length > 0;
        const evolution = displayEvolution(pet);
        const form = pet.forms.find((entry) => entry.evolution === evolution) ?? pet.forms[0];
        const url = form ? staticAssetUrl(manifest, form.assetKey) : null;
        const active = pet.copies.some((copy) => copy.active);
        return (
          <button
            key={pet.definitionId}
            type="button"
            className="inv-cell art-cell pet-cell"
            data-rarity={pet.rarity}
            data-paint={rarityPaint(pet.rarity)}
            data-owned={owned ? 'true' : 'false'}
            data-selected={pet.definitionId === selectedId ? 'true' : 'false'}
            data-testid={`pet-cell-${pet.definitionId}`}
            style={rarityStyle(pet.rarity)}
            aria-pressed={pet.definitionId === selectedId}
            aria-label={`${pet.name}, ${rarityLabel(pet.rarity)}, ${pet.stars} estrelas${
              owned ? `, evolução ${evolution}` : ', não obtido'
            }`}
            onClick={() => onSelect(pet.definitionId)}
          >
            <span className="inv-cell-ring" aria-hidden="true" />
            <span className="inv-cell-art">
              {url ? (
                <img src={url} alt="" loading="lazy" draggable={false} />
              ) : (
                <span className="inv-cell-noart" aria-hidden="true">
                  ?
                </span>
              )}
            </span>
            {/* Five slots, always: filled ones come from the rarity, and the
                empty ones say how far the scale goes. */}
            <PetStars stars={pet.stars} slots={pet.starSlots} testId={pet.definitionId} />
            {owned ? (
              <>
                {active ? (
                  <span
                    className="inv-badge"
                    data-corner="top-left"
                    data-variant="strong"
                    data-testid={`pet-active-${pet.definitionId}`}
                    title="Pet ativo"
                  >
                    E
                  </span>
                ) : null}
                <span
                  className="inv-badge"
                  data-corner="bottom-left"
                  data-testid={`pet-evolution-${pet.definitionId}`}
                  title={`Evolução ${evolution}`}
                >
                  {`EV.${evolution}`}
                </span>
                <span
                  className="inv-badge"
                  data-corner="bottom-right"
                  data-testid={`pet-quantity-${pet.definitionId}`}
                  title={pet.copies.length === 1 ? '1 cópia' : `${pet.copies.length} cópias`}
                >
                  {`×${pet.copies.length}`}
                </span>
              </>
            ) : (
              <span className="art-cell-missing" data-testid={`pet-missing-${pet.definitionId}`}>
                não obtido
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
